import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Zap, Shield, Code, Rocket, FileImage, Palette, Lock, Clock, Cpu, Database, ArrowRight, Workflow, BookOpen, Sparkles, Move, Layers, Type, Wand2 } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { useLanguage } from '../i18n';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, dir } = useLanguage();
  const [showImages, setShowImages] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const typingPhrases = useMemo(() => [
    t('pages.landing.hero.title'),
    t('pages.landing.hero.typing.creative'),
    t('pages.landing.hero.typing.dynamic'),
    t('pages.landing.hero.typing.scalable'),
    t('pages.landing.hero.typing.bestArabic')
  ], [t]);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);

  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  
  const images = [
    { src: '/examples/IMG_2220.jpg', alt: 'Example 1' },
    { src: '/examples/IMG_2221.jpg', alt: 'Example 2' },
    { src: '/examples/IMG_2222.jpg', alt: 'Example 3' },
    { src: '/examples/slider/image1.jpg', alt: 'Example 4' },
    { src: '/examples/slider/image2.jpg', alt: 'Example 5' },
    { src: '/examples/slider/image3.jpg', alt: 'Example 6' },
    { src: '/examples/slider/image4.jpg', alt: 'Example 7' },
    { src: '/examples/slider/image5.jpg', alt: 'Example 8' },
  ];
  
  const ACTIVE_IMAGE_COUNT = 5;

  const positions = [
    { top: 'top-16', left: 'left-2', size: 'w-16', mdTop: 'md:top-10', mdLeft: 'md:left-10', mdSize: 'md:w-32' },
    { top: 'top-20', right: 'right-2', size: 'w-16', mdTop: 'md:top-32', mdRight: 'md:right-16', mdSize: 'md:w-40' },
    { top: 'top-[45%]', left: 'left-1', size: 'w-14', mdTop: 'md:top-1/2', mdLeft: 'md:left-4', mdSize: 'md:w-24' },
    { top: 'top-[50%]', right: 'right-1', size: 'w-14', mdTop: 'md:top-1/3', mdRight: 'md:right-8', mdSize: 'md:w-32' },
    { top: 'bottom-[15%]', left: 'left-3', size: 'w-16', mdTop: 'md:bottom-40', mdLeft: 'md:left-20', mdSize: 'md:w-36' },
    { top: 'bottom-[12%]', right: 'right-3', size: 'w-14', mdTop: 'md:bottom-32', mdRight: 'md:right-24', mdSize: 'md:w-28' },
    { top: 'bottom-1/4', left: 'left-32', size: 'w-28', className: 'hidden xl:block' },
    { top: 'top-20', right: '1/4', size: 'w-36', className: 'hidden xl:block' },
  ];
  
  useEffect(() => {
    // Show images immediately
    setShowImages(true);
  }, []);

  useEffect(() => {
    if (!typingPhrases.length) return;
    const currentPhrase = typingPhrases[phraseIndex % typingPhrases.length] || '';
    const typingSpeed = 90;
    const deletingSpeed = 55;
    const holdDuration = 1600;
    let timeout: ReturnType<typeof setTimeout>;

    if (!isDeleting && displayText.length < currentPhrase.length) {
      timeout = setTimeout(() => {
        setDisplayText(currentPhrase.slice(0, displayText.length + 1));
      }, typingSpeed);
    } else if (!isDeleting && displayText.length === currentPhrase.length) {
      timeout = setTimeout(() => setIsDeleting(true), holdDuration);
    } else if (isDeleting && displayText.length > 0) {
      timeout = setTimeout(() => {
        setDisplayText(currentPhrase.slice(0, displayText.length - 1));
      }, deletingSpeed);
    } else if (isDeleting && displayText.length === 0) {
      timeout = setTimeout(() => {
        setIsDeleting(false);
        setPhraseIndex((prev) => (prev + 1) % typingPhrases.length);
      }, 300);
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, phraseIndex, typingPhrases]);

  useEffect(() => {
    setDisplayText('');
    setIsDeleting(false);
    setPhraseIndex(0);
  }, [typingPhrases]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    
    Object.keys(sectionRefs.current).forEach((key) => {
      const element = sectionRefs.current[key];
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => new Set(prev).add(key));
              entry.target.classList.add('animate-in');
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px',
        }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);
  
  useEffect(() => {
    // Change active image every 3 seconds - show 3 at a time
    const interval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % images.length);
    }, 1400);
    
    return () => clearInterval(interval);
  }, [images.length]);
  
  // Get the 3 active images
  const getActiveImages = () => {
    const activeImages = [];
    for (let i = 0; i < ACTIVE_IMAGE_COUNT; i++) {
      const index = (activeImageIndex + i) % images.length;
      activeImages.push(index);
    }
    return activeImages;
  };

  const pricingPlans = [
    { id: 'pack0', credits: 100, price: 15, popular: false },
    { id: 'pack1', credits: 350, price: 25, popular: true },
    { id: 'pack2', credits: 800, price: 35, popular: false },
    { id: 'pack3', credits: 1500, price: 50, popular: false },
  ];

  const features = [
    { icon: FileImage, titleKey: 'pages.landing.features.items.autoGenerate.title', descKey: 'pages.landing.features.items.autoGenerate.desc' },
    { icon: Palette, titleKey: 'pages.landing.features.items.customFonts.title', descKey: 'pages.landing.features.items.customFonts.desc' },
    { icon: Zap, titleKey: 'pages.landing.features.items.fast.title', descKey: 'pages.landing.features.items.fast.desc' },
    { icon: Shield, titleKey: 'pages.landing.features.items.secure.title', descKey: 'pages.landing.features.items.secure.desc' },
    { icon: Code, titleKey: 'pages.landing.features.items.api.title', descKey: 'pages.landing.features.items.api.desc' },
    { icon: Rocket, titleKey: 'pages.landing.features.items.production.title', descKey: 'pages.landing.features.items.production.desc' },
  ];

  return (
    <div className="min-h-screen bg-transparent" dir={dir}>
      <AppHeader />
      
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-4 py-20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <img 
            src="/auth-pattern.png" 
            alt="Background Pattern" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="max-w-7xl mx-auto w-full relative z-10">
          {/* Floating Images - Show 3 at a time */}
          {showImages && (() => {
            const activeIndices = getActiveImages();
            return (
              <>
                {images.map((image, index) => {
                  const isActive = activeIndices.includes(index);
                  return (
                    <div 
                      key={index}
                      className={`floating-image absolute ${positions[index].top} ${positions[index].left || ''} ${positions[index].right || ''} ${positions[index].size} ${positions[index].mdTop} ${positions[index].mdLeft || ''} ${positions[index].mdRight || ''} ${positions[index].mdSize || ''} ${positions[index].className || ''} aspect-[4/5] md:aspect-[4/5] transition-opacity duration-800`}
                      style={{ 
                        opacity: isActive ? 1 : 0,
                        pointerEvents: isActive ? 'auto' : 'none'
                      }}
                    >
                      <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl group">
                        <img src={image.src} alt={image.alt} className="w-full h-full object-cover" />
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}

          {/* Main Content */}
          <div className="relative z-10 text-center max-w-3xl mx-auto px-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-semibold mb-6">
              <Rocket className="w-4 h-4" />
              {t('pages.landing.hero.badge')}
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-blue-400 animate-fade-in leading-tight">
              <span>{displayText}</span>
              <span className="typing-caret" aria-hidden="true" />
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('pages.landing.hero.subtitle')}
            </p>
            
            <div className="flex items-center justify-center gap-4 mb-8">
              <button
                onClick={() => navigate('/signup')}
                className="glow-button px-10 py-4 text-lg font-bold rounded-full transition-all duration-300 flex items-center gap-2"
              >
                <Rocket className="w-5 h-5" />
                {t('pages.landing.hero.cta')}
              </button>
              <a
                href="#features"
                className="px-8 py-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl font-bold text-lg transition-colors"
              >
                {t('pages.landing.hero.howItWorks')}
              </a>
            </div>
            <p className="text-sm text-gray-500">{t('pages.landing.hero.freeTrial')}</p>
          </div>
        </div>
      </section>

      {/* Creative Automation Section */}
      <section 
        ref={(el) => { sectionRefs.current['creative'] = el; }}
        className="bg-gradient-to-b from-black via-zinc-900 to-black py-20 scroll-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-semibold mb-6 animate-slide-up">
              <Sparkles className="w-4 h-4" />
              {t('pages.landing.creativeAutomation.badge')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent animate-slide-up" style={{ animationDelay: '0.1s' }}>
              {t('pages.landing.creativeAutomation.title')}
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed animate-slide-up" style={{ animationDelay: '0.2s' }}>
              {t('pages.landing.creativeAutomation.subtitle')}
            </p>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.3s' }}>
              {t('pages.landing.creativeAutomation.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Drag & Drop Editor Section */}
      <section 
        ref={(el) => { sectionRefs.current['editor'] = el; }}
        className="bg-gradient-to-b from-zinc-900 to-black py-20 scroll-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-right">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-semibold mb-6">
                <Move className="w-4 h-4" />
                {t('pages.landing.editor.badge')}
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {t('pages.landing.editor.title')}
              </h2>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                {t('pages.landing.editor.subtitle')}
              </p>
              <p className="text-lg text-gray-400 mb-8">
                {t('pages.landing.editor.description')}
              </p>
            </div>
            <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 animate-slide-left hover:border-blue-500/50 transition-all duration-300">
              <div className="flex items-center justify-center h-64 bg-black/50 rounded-lg relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <Layers className="w-24 h-24 text-blue-400 opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section 
        ref={(el) => { sectionRefs.current['features'] = el; }}
        id="features" 
        className="bg-gradient-to-b from-black to-zinc-900 py-20 scroll-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent">{t('pages.landing.features.title')}</h2>
            <p className="text-gray-400 text-lg">{t('pages.landing.features.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div 
                key={idx} 
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-blue-500/50 transition-all hover:shadow-lg hover:shadow-blue-500/10 hover:-translate-y-1 animate-fade-in-up"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t(feature.titleKey)}</h3>
                <p className="text-gray-400">{t(feature.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Integration Section */}
      <section 
        ref={(el) => { sectionRefs.current['api'] = el; }}
        className="bg-gradient-to-b from-black to-zinc-900 py-20 scroll-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-green-900/40 via-blue-900/40 to-purple-900/40 border-2 border-green-500/50 rounded-2xl p-12 relative overflow-hidden animate-scale-in">
            <div className="absolute inset-0 bg-gradient-to-r from-green-600/10 via-blue-600/10 to-purple-600/10"></div>
            <div className="relative z-10">
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold text-white mb-4 bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">{t('pages.landing.api.title')}</h2>
                <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                  {t('pages.landing.api.subtitle')}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-black/40 rounded-xl p-6 border border-green-500/30 text-center hover:border-green-400/50 transition-all hover:scale-105">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileImage className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{t('pages.landing.api.imageApi')}</h3>
                  <p className="text-gray-400 text-sm">{t('pages.landing.api.imageApiDesc')}</p>
                </div>
                <div className="bg-black/40 rounded-xl p-6 border border-blue-500/30 text-center hover:border-blue-400/50 transition-all hover:scale-105">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Code className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{t('pages.landing.api.restApi')}</h3>
                  <p className="text-gray-400 text-sm">{t('pages.landing.api.restApiDesc')}</p>
                </div>
                <div className="bg-black/40 rounded-xl p-6 border border-purple-500/30 text-center hover:border-purple-400/50 transition-all hover:scale-105">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Workflow className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{t('pages.landing.api.urlApi')}</h3>
                  <p className="text-gray-400 text-sm">{t('pages.landing.api.urlApiDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Design Section */}
      <section 
        ref={(el) => { sectionRefs.current['custom'] = el; }}
        className="bg-gradient-to-b from-zinc-900 to-black py-20 scroll-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
              {t('pages.landing.customDesign.title')}
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              {t('pages.landing.customDesign.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 hover:border-blue-500/50 transition-all hover:shadow-xl hover:shadow-blue-500/20 animate-slide-right">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-lg flex items-center justify-center mb-6">
                <Wand2 className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{t('pages.landing.customDesign.dynamicElements.title')}</h3>
              <p className="text-gray-400 text-lg leading-relaxed">{t('pages.landing.customDesign.dynamicElements.desc')}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 hover:border-purple-500/50 transition-all hover:shadow-xl hover:shadow-purple-500/20 animate-slide-left">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg flex items-center justify-center mb-6">
                <Type className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">{t('pages.landing.customDesign.fontControl.title')}</h3>
              <p className="text-gray-400 text-lg mb-4 leading-relaxed">{t('pages.landing.customDesign.fontControl.desc')}</p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>{t('pages.landing.customDesign.fontControl.googleFonts')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>{t('pages.landing.customDesign.fontControl.uploadFonts')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>{t('pages.landing.customDesign.fontControl.fullControl')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* API Documentation Section */}
      <section 
        ref={(el) => { sectionRefs.current['docs'] = el; }}
        className="bg-gradient-to-b from-zinc-900 to-black py-20 scroll-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-900/40 via-indigo-900/40 to-purple-900/40 border-2 border-blue-500/50 rounded-2xl p-12 relative overflow-hidden animate-scale-in">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-indigo-600/10 to-purple-600/10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600/20 to-indigo-600/20 rounded-full flex items-center justify-center">
                  <BookOpen className="w-8 h-8 text-blue-400" />
                </div>
              </div>
              <h2 className="text-4xl font-bold text-white mb-4 text-center bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">{t('pages.landing.documentation.title')}</h2>
              <p className="text-gray-300 text-lg mb-8 text-center max-w-2xl mx-auto leading-relaxed">
                {t('pages.landing.documentation.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => navigate('/api-docs')}
                  className="px-8 py-4 bg-white text-black rounded-xl font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 shadow-xl inline-flex items-center gap-2"
                >
                  <BookOpen className="w-5 h-5" />
                  {t('pages.landing.documentation.viewDocs')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section 
        ref={(el) => { sectionRefs.current['pricing'] = el; }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 scroll-section" 
        id="pricing"
      >
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">{t('pages.landing.pricing.title')}</h2>
          <p className="text-gray-400 text-lg">{t('pages.landing.pricing.subtitle')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pricingPlans.map((plan, idx) => (
            <div
              key={plan.id}
              className={`bg-zinc-900 border-2 rounded-xl p-6 relative transition-all hover:scale-105 animate-fade-in-up ${
                plan.popular
                  ? 'border-blue-500 scale-105 shadow-xl shadow-blue-500/20'
                  : 'border-zinc-800 hover:border-blue-500/50'
              }`}
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              {plan.popular && (
                <div className="absolute -top-4 right-1/2 transform translate-x-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                  {t('pages.landing.pricing.popular')}
                </div>
              )}
              <div className="text-center mb-6">
                <div className="text-5xl font-bold text-white mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{plan.credits}</div>
                <div className="text-gray-400 text-sm">{t('pages.landing.pricing.credits')}</div>
              </div>
              <div className="text-center mb-6">
                <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-1">${plan.price}</div>
              </div>
              <button
                onClick={() => navigate('/signup')}
                className={`w-full py-3 rounded-lg font-bold transition-all hover:scale-105 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                    : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                }`}
              >
                {t('pages.landing.pricing.buyNow')}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Companies Section */}
      <section 
        ref={(el) => { sectionRefs.current['companies'] = el; }}
        className="bg-gradient-to-b from-black to-zinc-900 py-20 scroll-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              {t('pages.landing.companies.title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center justify-items-center">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 hover:border-blue-500/50 transition-all hover:shadow-xl hover:shadow-blue-500/20 w-full max-w-sm animate-fade-in-up">
              <div className="flex items-center justify-center mb-4 h-24">
                <img 
                  src="/alkhabar-logo.png" 
                  alt="Alkhabar Online" 
                  className="max-h-20 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                />
              </div>
              <h3 className="text-xl font-bold text-white text-center mb-2">
                {t('pages.landing.companies.alkhabar.name')}
              </h3>
              <p className="text-gray-400 text-center text-sm">
                {t('pages.landing.companies.alkhabar.nameAr')}
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 hover:border-blue-500/50 transition-all hover:shadow-xl hover:shadow-blue-500/20 w-full max-w-sm animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center justify-center mb-4 h-24">
                <img 
                  src="/bayg-logo.png" 
                  alt="Bahrain Asian Youth Games" 
                  className="max-h-20 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                />
              </div>
              <h3 className="text-xl font-bold text-white text-center mb-2">
                {t('pages.landing.companies.bayg.name')}
              </h3>
              <p className="text-gray-400 text-center text-sm">
                {t('pages.landing.companies.bayg.nameAr')}
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 hover:border-blue-500/50 transition-all hover:shadow-xl hover:shadow-blue-500/20 w-full max-w-sm animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center justify-center mb-4 h-24">
                <img 
                  src="/btc-logo.png" 
                  alt="Bahrain Turf Club" 
                  className="max-h-20 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                />
              </div>
              <h3 className="text-xl font-bold text-white text-center mb-2">
                {t('pages.landing.companies.btc.name')}
              </h3>
              <p className="text-gray-400 text-center text-sm">
                {t('pages.landing.companies.btc.nameAr')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        ref={(el) => { sectionRefs.current['cta'] = el; }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 scroll-section"
      >
        <div className="bg-gradient-to-r from-blue-900/40 via-purple-900/40 to-pink-900/40 border-2 border-blue-500/50 rounded-2xl p-12 text-center relative overflow-hidden animate-scale-in">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
          <div className="relative z-10">
            <h2 className="text-4xl font-bold text-white mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">{t('pages.landing.cta.title')}</h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">{t('pages.landing.cta.subtitle')}</p>
            <button
              onClick={() => navigate('/signup')}
              className="px-8 py-4 bg-white text-black rounded-xl font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 inline-flex items-center gap-2 shadow-xl"
            >
              <Rocket className="w-5 h-5" />
              {t('pages.landing.cta.button')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-6 sm:py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-6">
            {/* Powered By */}
            <div className="text-center md:text-right">
              <div className="text-gray-400 text-sm mb-2">{t('pages.landing.footer.poweredBy')}</div>
              <a 
                href="https://www.audome.io/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 font-semibold text-lg transition-colors"
              >
                Audome Technology
              </a>
            </div>

            {/* Contact */}
            <div className="text-center">
              <div className="text-gray-400 text-sm mb-2">{t('pages.landing.footer.contact')}</div>
              <a 
                href="mailto:ahmed@audome.io" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                ahmed@audome.io
              </a>
            </div>

            {/* API Documentation */}
            <div className="text-center">
              <div className="text-gray-400 text-sm mb-2">{t('pages.landing.footer.developers')}</div>
              <button
                onClick={() => navigate('/api-docs')}
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                {t('pages.landing.footer.apiDocs')}
              </button>
            </div>

            {/* Legal */}
            <div className="text-center">
              <div className="text-gray-400 text-sm mb-2">{t('pages.landing.footer.legal')}</div>
              <div className="flex flex-col gap-2 items-center">
                <button
                  onClick={() => navigate('/terms')}
                  className="text-gray-300 hover:text-white transition-colors text-sm font-medium"
                >
                  {t('pages.landing.footer.terms')}
                </button>
                <button
                  onClick={() => navigate('/privacy')}
                  className="text-gray-300 hover:text-white transition-colors text-sm font-medium"
                >
                  {t('pages.landing.footer.privacy')}
                </button>
              </div>
            </div>

            {/* Follow Us */}
            <div className="text-center md:text-left">
              <div className="text-gray-400 text-sm mb-2">{t('pages.landing.footer.follow')}</div>
              <div className="flex items-center justify-center md:justify-start gap-4">
                <a
                  href="https://twitter.com/audome"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                  aria-label="Twitter"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                  </svg>
                </a>
                <a
                  href="https://facebook.com/audome"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center text-gray-500 text-sm pt-6 border-t border-zinc-800">
            {t('pages.landing.footer.copyright')}
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
